import { Deferred } from 'utils/async';
import NetConnection from './connection';
import { RECONNECT_TIMEOUT } from 'constants/network';
import { wrapWithChunking } from './chunk-stream';
/** WebRTC peer connection. */
export class RTCPeerConn extends NetConnection {
    constructor(id, peer) {
        super(id);
        this.signalDeferred = new Deferred();
        this.onSignal = (signal) => {
            this.signalDeferred.resolve(signal);
            this.emit('signal', signal);
        };
        this.onStateChange = (state) => {
            if (state === 'disconnected') {
                this.emit('disconnect');
                this.once('connect', () => this.emit('reconnect'));
            }
        };
        this.onError = (error) => {
            if (error.code === 'CHUNK_DECODE_ERROR' && !this.isAuthed()) {
                // HACK: Send NetworkDisconnectReason.VersionMismatch using old
                // network protocol without chunking.
                const dc = this.getPrimaryDataChannel();
                dc.send(new Uint8Array([51]));
                dc.send(Buffer.from('RPC{"type":0,"id":2,"name":"kickClient","args":[4]}'));
                setTimeout(() => {
                    this.close();
                }, 0);
                return;
            }
            super.onError(error);
        };
        this.peer = peer;
        // Chunk messages for RTC connections since DataChannels don't automatically
        // handle this for us.
        const chunkedPeerStream = wrapWithChunking(peer);
        this.configureStream(chunkedPeerStream);
        this.peer.reconnectTimer = RECONNECT_TIMEOUT;
        this.onConnect = this.onConnect.bind(this);
        this.peer.on('close', this.close);
        this.peer.on('error', this.onError);
        this.peer.on('iceStateChange', this.onStateChange);
        this.peer.once('connect', this.onConnect);
        if (this.peer.connected) {
            this.onConnect();
        }
        else {
            this.peer.on('signal', this.onSignal);
        }
    }
    onConnect() {
        super.onConnect();
        const dc = this.getPrimaryDataChannel();
        if (dc) {
            dc.addEventListener('bufferedamountlow', () => {
                console.warn(`[RTCPeerConn] 'bufferedamountlow' event for ${this.id}`);
            });
        }
    }
    onClose() {
        super.onClose();
        this.peer.destroy();
        this.peer.removeAllListeners();
    }
    async getSignal() {
        return this.signalDeferred.promise;
    }
    signal(signal) {
        if (this.peer) {
            this.peer.signal(signal);
        }
    }
    receive(data) {
        // HACK: Workaround simple-peer bug where data is received before
        // 'connect' event
        if (!this.connected) {
            console.warn('[RTCPeerConn] Received RTCDataChannel data prior to connect event.');
            this.once('connect', () => this.receive(data));
            return;
        }
        super.receive(data);
    }
    getIP() {
        return this.peer.remoteAddress;
    }
    getPort() {
        return this.peer.remotePort;
    }
    getPrimaryDataChannel() {
        return this.peer._channel;
    }
    /** Whether the DataChannel buffer can accept more data. */
    readyToReceive() {
        const dc = this.getPrimaryDataChannel();
        return dc ? dc.bufferedAmount < dc.bufferedAmountLowThreshold : false;
    }
    /** Wait for DataChannel buffer to fall below byte threshold. */
    waitUntilReady() {
        if (this.readyToReceive())
            return Promise.resolve();
        const dc = this.getPrimaryDataChannel();
        if (!dc)
            return Promise.reject();
        return new Promise(resolve => {
            const done = () => {
                this.off('close', done);
                dc.removeEventListener('bufferedamountlow', done);
                resolve();
            };
            this.on('close', done);
            dc.addEventListener('bufferedamountlow', done);
        });
    }
}
